import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { NGXLogger } from 'ngx-logger';
import { first } from 'rxjs/operators';
import { AuthorizationService } from '../../../authorization/authorization.service';
import {
  ICreateSalesLeadResult,
  ILeadProduct,
  IProductSalesLead,
  ISalesLead,
  SalesLeadDataAccessService,
  SalesLeadEventId,
} from './sales-lead-data-access.service';

@Injectable({
  providedIn: 'root',
})
export class SalesLeadService {
  constructor(
    private _salesLeadDataAccessService: SalesLeadDataAccessService,
    private _authorizationService: AuthorizationService,
    private _logger: NGXLogger,
  ) {}

  public createSampleSalesLead(product: ILeadProduct): Promise<void> {
    return this._createLeadForProducts('LEAD-SWS-AMER-MRKPLC-SMPL-DWNLD', [product]);
  }

  public createMapShareLead(products?: ILeadProduct[]): Promise<void> {
    if (products) {
      return this._createLeadForProducts('LEAD-SWS-AMER-MRKPLC-SHARE-MAP', products);
    } else {
      return this._createLeadForProducts('LEAD-SWS-AMER-MRKPLC-SHARE-MAP');
    }
  }

  public contactSales(contactSalesData: ISalesLead): Promise<ICreateSalesLeadResult | null | undefined> {
    return this._createLeadForSales(contactSalesData);
  }

  private async _createLeadForProducts(eventId: SalesLeadEventId, products?: ILeadProduct[]): Promise<void> {
    const lead: IProductSalesLead = (await this._createLead()) as IProductSalesLead;
    lead.eventIds = [eventId];
    lead.webFormDownloads = products ? products : [];
    // This is intended to be a fire and forget API call.  We do not return anything.
    try {
      await this._salesLeadDataAccessService.createSalesLead(lead).toPromise();
    } catch (e) {
      if (e instanceof HttpErrorResponse) {
        this._logger.error('Unable to create product sales lead.', {
          httpErrorResponse: e,
          lead,
        });
      } else {
        const err = e as Error;
        this._logger.error('Unable to create product sales lead.', {
          error: {
            message: err.message,
            stack: err.stack,
            name: err.name,
          },
          lead,
        });
      }
    }
  }

  private async _createLeadForSales(contactSalesData: ISalesLead): Promise<ICreateSalesLeadResult | undefined | null> {
    try {
      return await this._salesLeadDataAccessService.createSalesLead(contactSalesData).toPromise();
    } catch (e) {
      if (e instanceof HttpErrorResponse) {
        this._logger.error('Unable to contact sales.', {
          httpErrorResponse: e,
          contactSalesData,
        });
      } else {
        const err = e as Error;
        this._logger.error('Unable to contact sales.', {
          error: {
            message: err.message,
            stack: err.stack,
            name: err.name,
          },
          contactSalesData,
        });
      }
      return null;
    }
  }

  private async _createLead(): Promise<Partial<IProductSalesLead>> {
    const user = await this._authorizationService.principal$.pipe(first()).toPromise();
    const identity = user?.identity;
    return {
      contactMeNow: true,
      email: identity?.email,
      firstName: identity?.givenName,
      lastName: identity?.familyName,
    } as Partial<IProductSalesLead>;
  }
}
